import React, { useState } from "react";
import { Button, Divider, Menu, MenuItem, Box, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle } from "@mui/material";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { Add, EditOutlined } from "@mui/icons-material";

import BreadcrumbLayout from "layouts/BreadcrumbLayout";
import CocCollectionBasicDetail from "../CocCollection/cocCollectionComponents/CocCollectionBasicDetail";
import { useAppMeta, useAuth } from "hooks";
import {
  CocCollectionDetailContext,
  useCocCollectionDetailContext,
} from "contexts/PaymentContexts/CocCollectionContext";
import {
  useAddFinanceCollectionMutation,
  useGetCocCollectionDetailQuery,
  useRejectCOCCollectionMutation,
} from "store/services/cocCollectionApi";
import CocCollectionDetailTab from "./cocCollectionComponents/CocCollectionDetailTab";
import BaseModal from "components/Shared/BaseModel/BaseModel";
import FinanceCollectionForm from "./FinanceCollection/FinanceCollectionForm";

const CocCollectionDetailScreen = () => {
  const { id } = useParams();
  const { isVendor } = useAuth();
  const breadcrumbs = [
    {
      title: "Counter Collection",
      path: isVendor
        ? "/app/vendor/counter-collection/list"
        : "/app/crm/coc-collection/list",
    },
    { title: "Counter Collection Detail" },
  ];

  const {
    data: CocCollectionDetail,
    isLoading: loadingCocCollectionDetail,
    error,
  } = useGetCocCollectionDetailQuery(id);

  useAppMeta("COC Collection Detail");

  return (
    <CocCollectionDetailContext.Provider
      value={{ CocCollectionDetail, loadingCocCollectionDetail }}
    >
      <BreadcrumbLayout
        breadcrumbs={breadcrumbs}
        isBusy={loadingCocCollectionDetail}
        headerActions={<PageActions />}
      >
        <BreadcrumbLayout.Error error={error} />
        <BreadcrumbLayout.Paper>
          <CocCollectionBasicDetail />
          <Divider />
          <CocCollectionDetailTab />
        </BreadcrumbLayout.Paper>
      </BreadcrumbLayout>
    </CocCollectionDetailContext.Provider>
  );
};

export default CocCollectionDetailScreen;

const PageActions = () => {
  const [openModal, setOpenModal] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [openRejectDialog, setOpenRejectDialog] = useState(false);
  const { isSystemAdmin, isAccountAdmin, isEntityAdmin } = useAuth();
  const { CocCollectionDetail, loadingCocCollectionDetail } = useCocCollectionDetailContext();

  const [addFinanceCollection, { isLoading: addingFinanceCollection }] = useAddFinanceCollectionMutation();
  const [rejectCocCollection, { isLoading: rejectingCocCollection }] = useRejectCOCCollectionMutation();

  const handleFinanceCollectionSubmit = async (payload) => {
    try {
      await addFinanceCollection({
        id: CocCollectionDetail._id,
        ...payload,
      }).unwrap();
      toast.success("Finance collection added successfully");
      setOpenModal(false);
    } catch (error) {
      toast.error(error?.data?.message ?? "Failed to add finance collection");
    }
  };

  const handleRejectCollection = async () => {
    try {
      await rejectCocCollection(CocCollectionDetail._id).unwrap();
      toast.success("COC collection rejected successfully");
      setOpenRejectDialog(false);
      setAnchorEl(null);
    } catch (error) {
      toast.error(error?.data?.message ?? "Failed to reject COC collection");
    }
  };

  const isCollectedOrRejected = CocCollectionDetail?.financeCollection?.isCollected !== undefined;
  const collectButtonLabel = isCollectedOrRejected
    ? CocCollectionDetail?.financeCollection?.isCollected==="Approved"
      ? "Approved"
      : "Rejected"
    : "Manage Collection";

  const isCollectButtonDisabled =
    loadingCocCollectionDetail ||
    addingFinanceCollection ||
    rejectingCocCollection ||
    isCollectedOrRejected;

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleCollectClick = () => {
    setOpenModal(true);
    setAnchorEl(null);
  };

  const handleRejectClick = () => {
    setOpenRejectDialog(true);
    setAnchorEl(null);
  };

  return (
    <>
      {(isSystemAdmin || isAccountAdmin || isEntityAdmin) && (
        <Box display="flex" gap={2} alignItems="center">
        <Button
  startIcon={<Add fontSize="small" />}
  variant="contained"
  color="primary"
  onClick={handleMenuOpen}
  disabled={isCollectButtonDisabled}
  sx={{
    // minWidth: "130px",
    // height: 32,
    borderRadius: "5px",
    textTransform: "none",
    fontSize: "1rem",
    px: 1.5,
    py: 0.5,
  }}
>
  {collectButtonLabel}
</Button>
          <Menu
  anchorEl={anchorEl}
  open={Boolean(anchorEl)}
  onClose={handleMenuClose}
  PaperProps={{
    elevation: 4,
    sx: {
      borderRadius: 2,
      minWidth: 160,
      mt: 1,
      p: 0.5,
    },
  }}
  anchorOrigin={{
    vertical: "bottom",
    horizontal: "right",
  }}
  transformOrigin={{
    vertical: "top",
    horizontal: "right",
  }}
>
  <MenuItem
    onClick={handleCollectClick}
    disabled={isCollectedOrRejected}
    sx={{ fontSize: "1rem", borderRadius: 1, minHeight: 36 }}
  >
        <Add fontSize="small" sx={{ mr: 1 }} />
    
    Approve
  </MenuItem>
  <MenuItem
    onClick={handleRejectClick}
    disabled={isCollectedOrRejected}
    sx={{ fontSize: "1rem", borderRadius: 1, minHeight: 36 }}
  >
        <EditOutlined fontSize="small" sx={{ mr: 1 }} />
    
    Reject
  </MenuItem>
</Menu>
        </Box>
      )}

      <BaseModal
        isOpen={openModal}
        onClose={() => setOpenModal(false)}
        title="Collect COC Amount"
      >
        <FinanceCollectionForm
          onSubmit={handleFinanceCollectionSubmit}
          isBusy={addingFinanceCollection}
        />
      </BaseModal>

      <Dialog
        open={openRejectDialog}
        onClose={() => setOpenRejectDialog(false)}
        aria-labelledby="reject-dialog-title"
        aria-describedby="reject-dialog-description"
      >
        <DialogTitle id="reject-dialog-title">Confirm Reject</DialogTitle>
        <DialogContent>
          <DialogContentText id="reject-dialog-description">
            Are you sure you want to reject this COC collection?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenRejectDialog(false)} color="secondary">
            Cancel
          </Button>
          <Button onClick={handleRejectCollection} color="primary" autoFocus>
            Reject
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
